import type { ReportActionState } from '@/features/invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonV2,
  IconShare,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, ReactNode, useMemo } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type ReasonTooltipProps = {
  reason?: string | null;
  children: ReactNode;
};

/**
 * Wraps `children` in a `Tooltip` only when `reason` is present, so callers
 * don't need to branch their whole render tree just to attach an optional
 * reason tooltip to a disabled button.
 */
const ReasonTooltip: FC<ReasonTooltipProps> = memo(({ reason, children }) => {
  const container = useGlobalContainerRef();

  if (!reason) return <>{children}</>;

  return (
    <Tooltip
      title={reason}
      overlayClassName={cx('editTooltip')}
      getPopupContainer={container}
    >
      <span>{children}</span>
    </Tooltip>
  );
});
ReasonTooltip.displayName = 'ReasonTooltip';

export type ShareButtonProps = {
  shareAction?: ReportActionState;
  onOpenShareViewers?: () => void;
  /**
   * Different call sites need different variants — bordered (secondary)
   * vs. borderless (transparent) — e.g. the detail drawer's title row wants
   * a border, while a list's bulk-action row does not. Defaults to bordered.
   */
  bordered?: boolean;
};

export const ShareButton: FC<ShareButtonProps> = memo(
  ({ shareAction, onOpenShareViewers, bordered = true }) => {
    const { t } = useTranslation();
    const variantProps = useMemo(
      () => (bordered ? { isSecondary: true } : { isTransparent: true }),
      [bordered]
    );

    if (!shareAction?.visible) return null;

    const isDisabled = !shareAction.allowed;

    return (
      <ReasonTooltip reason={isDisabled ? shareAction.reason : null}>
        <ButtonV2
          size='sm'
          {...variantProps}
          isDisabled={isDisabled}
          onClick={isDisabled ? undefined : onOpenShareViewers}
          leftIcon={<IconShare size={0} />}
          omitLeftIconPadding
          testId='invoice-report-share-button'
        >
          {t('invoice_report_share.title')}
        </ButtonV2>
      </ReasonTooltip>
    );
  }
);

ShareButton.displayName = 'ShareButton';
